import { inject, Injectable } from '@angular/core';
import { InjectableSuperclass } from '@sama/ng-core';
import { combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AxesHelper, Quaternion } from 'three';
import { CalibrationOptions } from '../../../../cross-team-shared/state/calibration-options-state';
import { SingleFrameCalibrationData } from '../../../../team-apis/otterdam/calibration-data';
import { CalibrationService } from '../../../misc-services/calibration.service';
import { PointCloudContentStore } from '../../../state/point-cloud-content-store';
import { ObjectWith } from '../../../utils/misc-utils';
import { RendererLayer, setRendererLayers } from '../../render-utils';
import { SceneControllerService } from '../../scene-controller.service';

@Injectable()
export class AxesService extends InjectableSuperclass {
  private axesHelpers = new Map<string, AxesHelper>();
  private originHelper = new AxesHelper(5);

  private calibrationService = inject(CalibrationService);
  private sceneControllerService = inject(SceneControllerService);
  private store = inject(PointCloudContentStore);

  constructor() {
    super();
    this.setUpSubscriptions();
    this.sceneControllerService.addToScene(this.originHelper);
  }

  private setUpSubscriptions() {
    this.subscribeTo(
      this.store('calibrationData').$.pipe(
        map(Object.keys),
        switchMap((videoOutputNames) =>
          combineLatest([
            ...videoOutputNames.map(this.getCalibrationData$.bind(this)),
          ]),
        ),
      ),
      this.updateCameraHelpers.bind(this),
    );

    this.subscribeTo(
      this.store('render')('calibrationOptions').$,
      this.updateVisibility,
    );
  }

  private getCalibrationData$(videoOutputName: string) {
    return this.calibrationService
      .getCameraData$(videoOutputName)
      .pipe(map((calibrationData) => ({ videoOutputName, calibrationData })));
  }

  private updateCameraHelpers(
    calibrationInfo: Array<{
      videoOutputName: string;
      calibrationData: SingleFrameCalibrationData;
    }>,
  ) {
    calibrationInfo.forEach(({ videoOutputName, calibrationData }) => {
      if (!this.axesHelpers.has(videoOutputName)) {
        const newHelper = new AxesHelper(3);
        this.axesHelpers.set(videoOutputName, newHelper);
        this.sceneControllerService.addToScene(newHelper);
      }

      const helper = this.axesHelpers.get(videoOutputName)!;
      helper.position.fromArray(calibrationData.position);
      helper.rotation.setFromQuaternion(
        new Quaternion(...calibrationData.rotation),
      );
    });
  }

  private updateVisibility(calibrationOptions: ObjectWith<CalibrationOptions>) {
    setRendererLayers([RendererLayer.Main], this.originHelper);

    Object.entries(calibrationOptions).forEach(
      ([videoOutputName, { showAxesHelper }]) => {
        const layers = showAxesHelper ? [RendererLayer.Main] : [];
        const helper = this.axesHelpers.get(videoOutputName);
        if (!helper) {
          return;
        }

        setRendererLayers(layers, helper);
      },
    );
  }
}
